import { useId } from 'react'
import type { ReactNode, SelectHTMLAttributes } from 'react'
import { fieldControl } from './formClasses'
import { IconChevronDown } from './Icons'
import { cn } from './cn'
import { humanizeLabel } from './humanizeLabel'

export { humanizeLabel }

export interface AsyncSelectOption {
  value: string
  /** Human label. If omitted, the value is humanized (snake/kebab → Title Case). */
  label?: ReactNode
  disabled?: boolean
}

export interface AsyncSelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: ReactNode
  hint?: ReactNode
  /** Validation/field error (red border + message). */
  error?: ReactNode
  options?: AsyncSelectOption[]
  /** Show a skeleton + disable while options load. */
  loading?: boolean
  /** Async-load failure. Shown as the field error + retry affordance. */
  loadError?: ReactNode
  /** Retry callback rendered when loadError is set. */
  onRetry?: () => void
  /** Text when not loading and options is empty. Default 'No options available'. */
  emptyLabel?: ReactNode
  /** Leading placeholder option. */
  placeholder?: string
  className?: string
}

/**
 * AsyncSelect — a <Select> that is loading/empty/error-aware for options coming
 * from a fetch. Keeps the combobox visible while loading (disabled + aria-busy);
 * never renders raw machine keys (missing labels are humanized). On `loadError`
 * shows the error + optional Retry.
 */
export function AsyncSelect({
  label,
  hint,
  error,
  options = [],
  loading = false,
  loadError,
  onRetry,
  emptyLabel = 'No options available',
  placeholder,
  className = '',
  disabled,
  ...props
}: AsyncSelectProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error || loadError ? `${id}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined
  const isEmpty = !loading && !loadError && options.length === 0
  const fieldError = (error ?? loadError) || undefined

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={id} className="mb-1 block text-sm font-medium text-ink-secondary">
          {label}
        </label>
      )}

      <div className="relative">
        <select
          id={id}
          disabled={disabled || loading || isEmpty}
          aria-invalid={fieldError ? true : undefined}
          aria-describedby={describedBy}
          aria-busy={loading || undefined}
          className={cn(fieldControl(fieldError), 'appearance-none pr-10', !isEmpty && 'cursor-pointer', className)}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {isEmpty ? (
            <option value="">{typeof emptyLabel === 'string' ? emptyLabel : 'No options available'}</option>
          ) : (
            options.map((o) => (
              <option key={o.value} value={o.value} disabled={o.disabled}>
                {o.label != null ? (typeof o.label === 'string' ? o.label : o.value) : humanizeLabel(o.value)}
              </option>
            ))
          )}
        </select>
        <IconChevronDown
          size={16}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted"
        />
      </div>

      {hint && !fieldError && <p id={hintId} className="mt-1 text-xs text-ink-muted">{hint}</p>}
      {fieldError && (
        <p id={errorId} role="alert" className="mt-1 flex items-center gap-2 text-sm text-err-ink">
          <span>{fieldError}</span>
          {loadError && onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="font-medium underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:shadow-focus"
            >
              Retry
            </button>
          )}
        </p>
      )}
    </div>
  )
}
